import axios from "axios";

// ADD: util para padronizar headers
const getAuthHeaders = () => ({
  "Screen-Name": "/NewLoad",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Function to fetch data with retry logic (from original Load.jsx)
export const fetchDataWithRetry = async (url, retries = 3, delay = 1000, axiosConfig = {}) => {
  try {
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
      ...axiosConfig,           // <- permite passar { params: { … } }
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 429 && retries > 0) {
      console.log(`Retrying... (${retries} left)`);
      await new Promise((res) => setTimeout(res, delay));
      return fetchDataWithRetry(url, retries - 1, delay * 2, axiosConfig);
    } else {
      console.error("Failed after retries:", error);
      throw error;
    }
  }
};

// Function to fetch partner companies
export const getPartnerCompanies = async () => {
  const partnerCompanyData = await fetchDataWithRetry(
    `https://new.cajuu.app/api/partnercompany`
  );
  return partnerCompanyData.data;
};

// Function to fetch shippers
export const getShippers = async () => {
  const shipperData = await fetchDataWithRetry(
    `https://new.cajuu.app/api/shipper`
  );
  return shipperData.data;
};

// Function to fetch car types
export const getCarTypes = async () => {
  const carTypeData = await fetchDataWithRetry(
    `https://new.cajuu.app/api/cartype`
  );
  return carTypeData.data;
};

// Function to fetch load statuses
export const getLoadStatuses = async () => {
  const loadStatusData = await fetchDataWithRetry(
    `https://new.cajuu.app/api/loadstatus`
  );
  return loadStatusData.data;
};

// Function to fetch users (drivers)
export const getUsers = async () => {
  const usersData = await fetchDataWithRetry(`https://new.cajuu.app/api/users`);
  return usersData.data;
};

// Main function to fetch transport data (from original Load.jsx)
export const fetchTransportData = async (params = {}) => {
  try {
    const [
      loadTransportsRes,
      partnerCompaniesRes,
      loadStatusesRes,
      usersRes,
    ] = await Promise.all([
      // passa params para paginação e filtros
      fetchDataWithRetry(`https://new.cajuu.app/api/loadtransport`, 3, 1000, { params }),
      fetchDataWithRetry(`https://new.cajuu.app/api/partnercompany`),
      fetchDataWithRetry(`https://new.cajuu.app/api/loadstatus`),
      fetchDataWithRetry(`https://new.cajuu.app/api/users`),
    ]);

    // payload paginado do Laravel vem como .data (itens), .meta, .links
    // const ltPayload = loadTransportsRes?.data ?? {};
    // const ltItems   = Array.isArray(ltPayload?.data) ? ltPayload.data : (Array.isArray(loadTransportsRes?.data) ? loadTransportsRes.data : []);
    const ltPayload = loadTransportsRes?.data ?? {};
    const listBox   = ltPayload;                  // já é o objeto com paginação
    const ltItems   = Array.isArray(listBox?.data) ? listBox.data : [];

    // Monta meta manualmente (com fallback para last_page)
    const total = Number(listBox?.total ?? 0);
    const per   = Number(listBox?.per_page ?? 10);
    const meta = {
      current_page: Number(listBox?.current_page ?? 1),
      per_page: per,
      total: total,
      last_page: Number(listBox?.last_page ?? Math.max(1, Math.ceil(total / (per || 1)))),
      next_page_url: listBox?.next_page_url ?? null,
      prev_page_url: listBox?.prev_page_url ?? null,
      path: listBox?.path ?? "",
    };

    const partnerCompanies = Object.fromEntries(
      (partnerCompaniesRes?.data ?? []).map((pc) => [pc.id, pc])
    );
    const loadStatuses = Object.fromEntries(
      (loadStatusesRes?.data ?? []).map((ls) => [ls.id, ls])
    );
    const users = Object.fromEntries((usersRes?.data ?? []).map((u) => [u.id, u]));

    // merge de campos relacionados (para cada item paginado)
    const merged = ltItems.map((lt) => ({
      ...lt,
      partner_company: partnerCompanies[lt.partner_company_id],
      load_status: loadStatuses[lt.load_status_id],
      user: lt.driver_id ? users[lt.driver_id] || "" : "",
    }));

    // IMPORTANTE: não ordenar aqui — backend já controla via sort_by/sort_direction
    return {
      items: merged,
      meta,          // agora vem preenchido
      links: [],     // opcional, não usado
      raw: loadTransportsRes,
    };
  } catch (error) {
    console.error("Error fetching transport data:", error);
    throw error;
  }
};

// Function to fetch load item details
export const getLoadItem = async (loadId) => {
  const userToken = localStorage.getItem("token");
  try {
    const loadItemResponse = await axios.get(
      `https://new.cajuu.app/api/loaditem/${loadId}`,
      {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
    return loadItemResponse.data.data;
  } catch (error) {
    console.error("Error fetching load item:", error);
    throw error;
  }
};

// Function to update load status
export const updateLoadStatus = async (loadId, newStatus) => {
  const userToken = localStorage.getItem("token");
  try {
    const response = await axios.put(
      `https://new.cajuu.app/api/loadtransport/${loadId}`,
      { load_status_id: newStatus },
      {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating load status:", error);
    throw error;
  }
};

// Function to update load details
export const updateLoadDetails = async (loadId, details) => {
  const userToken = localStorage.getItem("token");
  try {
    const response = await axios.put(
      `https://new.cajuu.app/api/loadtransport/${loadId}`,
      details,
      {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating load details:", error);
    throw error;
  }
};
